import { Component, inject, Input, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { ModalController } from '@ionic/angular';
import {
  SelectionMarkColor,
  formatAgeRangeHints,
  isValidBirthDateRange,
  resolveBirthDateReference,
  toDateInputValue,
} from '@tcc-mono/vandijk/shared/data-access/models';
import { AnimalSelectionRepository } from '@tcc-mono/vandijk/shared/data-access/state';
import { ToastService } from '@tcc-mono/shared/ui/services';
import { take } from 'rxjs';

@Component({
  selector: 'tcc-mono-animal-selection-filter-colors-dialog',
  templateUrl: './animal-selection-filter-colors-dialog.component.html',
  styleUrls: ['./animal-selection-filter-colors-dialog.component.scss']
})
export class AnimalSelectionFilterColorsDialogComponent implements OnInit {
  private readonly _fb: FormBuilder = inject(FormBuilder);
  private readonly _modalCtrl: ModalController = inject(ModalController);
  private readonly _repo: AnimalSelectionRepository = inject(AnimalSelectionRepository);
  private readonly _toast: ToastService = inject(ToastService);

  @Input() selectionId: number;
  @Input() colors: SelectionMarkColor[];
  /** created_at van de selectie (voor leeftijd-hint bij aanmaak). */
  @Input() selectionCreatedAt?: string | null;

  form: FormGroup;
  saving = false;

  ngOnInit(): void {
    this.form = this._fb.group({
      items: this._fb.array(
        (this.colors || []).map(c =>
          this._fb.group({
            color_name: [c.value],
            used: [c.used !== false],
            min_birth_date: [toDateInputValue(c.min_birth_date)],
            max_birth_date: [toDateInputValue(c.max_birth_date)],
          })
        )
      )
    });
  }

  get items(): FormArray {
    return this.form.get('items') as FormArray;
  }

  getColorName(index: number): string {
    return this.colors?.[index]?.name ?? '';
  }

  ageRangeHints(minBirthDate?: string | null, maxBirthDate?: string | null): string[] {
    return formatAgeRangeHints(
      minBirthDate,
      maxBirthDate,
      resolveBirthDateReference(this.selectionCreatedAt),
    );
  }

  save(): void {
    if (this.form.invalid || this.saving || !this.selectionId) return;

    for (const ctrl of this.items.controls) {
      if (!ctrl.get('used')?.value) {
        continue;
      }
      const min = ctrl.get('min_birth_date')?.value;
      const max = ctrl.get('max_birth_date')?.value;
      if (!min && !max) {
        this._toast.show({
          header: 'Ongeldige datum',
          message: 'Actieve kleuren vereisen minimaal één geboortedatum-grens.',
          color: 'danger',
        });
        return;
      }
      if (!isValidBirthDateRange(min, max)) {
        this._toast.show({
          header: 'Ongeldige datum',
          message: 'Van-datum mag niet na tot-datum liggen.',
          color: 'danger',
        });
        return;
      }
    }

    this.saving = true;
    const body = {
      selection_mark_colors: this.items.controls.map((ctrl) => ({
        color_name: ctrl.get('color_name')?.value,
        used: ctrl.get('used')?.value ?? true,
        min_birth_date: ctrl.get('used')?.value ? (ctrl.get('min_birth_date')?.value || null) : null,
        max_birth_date: ctrl.get('used')?.value ? (ctrl.get('max_birth_date')?.value || null) : null,
      }))
    };
    this._repo
      .updateSelectionMarkColors(this.selectionId, body)
      .pipe(take(1))
      .subscribe({
        next: () => this._modalCtrl.dismiss(true),
        error: () => (this.saving = false)
      });
  }

  close(): void {
    this._modalCtrl.dismiss(false);
  }
}
